/**
 * Centralized error handling with user-friendly messages
 */

import { logger } from './logger';
import { toast } from '@/hooks/use-toast';

export interface ErrorContext {
  action?: string;
  component?: string;
  userId?: string;
  workspaceId?: string;
  [key: string]: any;
}

/**
 * Extract user-friendly error message from various error types
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  return fallback;
}

/**
 * Handle Supabase errors with specific messages
 */
export function getSupabaseErrorMessage(error: any): string {
  if (!error) return 'Unknown error';
  
  // Supabase error structure
  if (error.message) {
    // Common Supabase errors
    if (error.message.includes('JWT')) {
      return 'Session expired. Please log in again.';
    }
    if (error.message.includes('permission denied') || error.message.includes('RLS')) {
      return 'You do not have permission to perform this action.';
    }
    if (error.message.includes('duplicate key')) {
      return 'This record already exists.';
    }
    if (error.message.includes('foreign key')) {
      return 'Invalid reference. Please refresh and try again.';
    }
    if (error.message.includes('column') && error.message.includes('does not exist')) {
      return 'Database schema error. Please contact support.';
    }
    return error.message;
  }
  
  if (error.error) {
    return getSupabaseErrorMessage(error.error);
  }
  
  return 'Database error occurred.';
}

/**
 * Show error toast and log error
 */
export function handleError(
  error: unknown,
  context: ErrorContext = {},
  showToast = true
): void {
  const message = getErrorMessage(error);
  const supabaseMessage = error && typeof error === 'object' && 'code' in error
    ? getSupabaseErrorMessage(error)
    : message;

  logger.error(supabaseMessage, error, context);

  if (showToast) {
    toast({
      title: context.action ? `${context.action} failed` : 'Error',
      description: supabaseMessage,
      variant: 'destructive',
    });
  }
}

/**
 * Wrap async function with error handling
 */
export function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: ErrorContext = {}
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, context);
      throw error; // Re-throw so caller can handle if needed
    }
  }) as T;
}

/**
 * Create a retry function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
}

